import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { UNSAFE_UpdateOnChange as SpotlightUpdateOnChange } from '@atlaskit/spotlight/update-on-change';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { Text } from '@atlaskit/primitives/compiled';

import { PlacementFetchProvider } from '@confluence/confluence-post-office-provider/entry-points/placement-fetch-provider.ts';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageData } from '@atlassian/post-office-headless-api/hooks/useMessageData';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { fg } from '@confluence/feature-gating';

import spotlightMediaPlaceholder from './assets/spotlight-media-placeholder.svg';

const MESSAGE_TEMPLATE_ID = 'confluence-native-tabs-toolbar-spotlight';
const PLACEMENT_ID = 'headless';

const styles = cssMap({
	media: {
		display: 'block',
		height: '135px',
		width: '295px',
	},
});

const messages = defineMessages({
	body: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.body',
		defaultMessage:
			'Structure related content into easy-to-scan tabs, so readers can quickly find what they need.',
		description: 'Body text for the Native Tabs toolbar spotlight.',
	},
	close: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.close',
		defaultMessage: 'Close',
		description:
			'Accessible label for the close button that dismisses the Native Tabs toolbar spotlight.',
	},
	done: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.done',
		defaultMessage: 'Done',
		description: 'Button text that dismisses the Native Tabs toolbar spotlight.',
	},
	title: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.title',
		defaultMessage: 'Organize your content with tabs',
		description: 'Title for the Native Tabs toolbar spotlight.',
	},
});

type TabsButtonSpotlightProps = {
	children: React.ReactElement;
};

type NativeTabsSpotlightMessage = {
	messageInstanceId: string;
};

type NativeTabsSpotlightCardProps = TabsButtonSpotlightProps & {
	dismissMessage: (messageInstanceId: string) => void;
	message: NativeTabsSpotlightMessage;
};

const NativeTabsSpotlightCard = ({
	children,
	dismissMessage,
	message,
}: NativeTabsSpotlightCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { onAcknowledged, onClosed } = usePostOfficeUserInteractions();
	const { PopoverContent } = useChoreographerAssets();

	const handleClose = useCallback(() => {
		void onClosed({ interactionIdentifier: 'close' });
		dismissMessage(message.messageInstanceId);
	}, [dismissMessage, message.messageInstanceId, onClosed]);

	const handleDone = useCallback(() => {
		void onAcknowledged({ interactionIdentifier: 'done' });
		dismissMessage(message.messageInstanceId);
	}, [dismissMessage, message.messageInstanceId, onAcknowledged]);

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<SpotlightUpdateOnChange />
			<PopoverContent dismiss={handleClose} isVisible offset={[0, 8]} placement="bottom-center">
				<SpotlightCard testId="native-tabs-toolbar-spotlight-card">
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(messages.title)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleClose} autoFocus={false} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<img css={styles.media} alt="" src={spotlightMediaPlaceholder} />
					</SpotlightMedia>
					<SpotlightBody>
						<Text>{formatMessage(messages.body)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={handleDone}>
								{formatMessage(messages.done)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};

const TabsButtonSpotlightMessage = ({ children }: TabsButtonSpotlightProps): React.JSX.Element => {
	const { message, PostOfficeMessage, dismissMessage } = useMessageData(
		{
			placementId: PLACEMENT_ID,
			messageTemplateId: MESSAGE_TEMPLATE_ID,
		},
		{
			dataOutsideWrapper: true,
		},
	);

	if (!PostOfficeMessage || !message) {
		return (
			<PopoverProvider>
				<PopoverTarget>{children}</PopoverTarget>
			</PopoverProvider>
		);
	}

	return (
		<PostOfficeMessage>
			{() => (
				<NativeTabsSpotlightCard dismissMessage={dismissMessage} message={message}>
					{children}
				</NativeTabsSpotlightCard>
			)}
		</PostOfficeMessage>
	);
};
export const TabsButtonSpotlight = ({ children }: TabsButtonSpotlightProps): React.JSX.Element => {
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (!fg('post-office_enable_conf-tabs-toolbar-spotlight')) {
		return children;
	}

	return (
		<PlacementFetchProvider>
			<TabsButtonSpotlightMessage>{children}</TabsButtonSpotlightMessage>
		</PlacementFetchProvider>
	);
};
